"use client";

import * as React from "react";

import { usePathname } from "next/navigation";

import { NavOrganization } from "@/components/nav-organization";
import { NavPrimary } from "@/components/nav-primary";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from "@/components/ui/sidebar";

import {
  Building,
  Building2,
  CodeXml,
  Coins,
  CreditCard,
  Key,
  Mail,
  MessageSquareMore,
  MonitorDown,
  ScrollText,
  Settings,
  TriangleAlert,
  User,
  Users,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import {
  LIBUM_DEV_MAIL_URL,
  LIBUM_DISCORD_URL,
  LIBUM_DOCS_POWERON_PRO_URL,
  LIBUM_FEEDBACK_URL,
  LIBUM_POWERON_PRO_EULA_URL,
  LIBUM_POWERON_PRO_INSTALL_URL,
} from "@/lib/constants";

import Discord from "@/components/icons/discord";
import PowerOnPro from "@/components/icons/poweron-pro";
import SparkleIcon from "@/components/icons/sparkle-icon";
import { findGroupsByBaseUrl, findMainTitleByBaseUrl } from "@/utils/utils";
import { useUserStore } from "@/stores/user";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useOrganizationStore } from "@/stores/organization";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const pathname = usePathname();
  const user = useUserStore((state) => state.user);
  const subscriptions = useOrganizationStore((state) => state.subscriptions);
  const isMobile = useIsMobile();

  const data = {
    user: {
      id: user?.id,
      name: user?.name,
      email: user?.email,
      avatar: user?.avatar,
    },
    navMain: [
      {
        title: "PowerOn Pro",
        url: "/poweron-pro",
        icon: PowerOnPro,
        groups: [
          {
            title: "General",
            pages: [
              {
                title: "Users",
                url: "/poweron-pro/users",
                icon: Users,
              },
            ],
          },
          {
            title: "Resources",
            className: "mt-auto",
            pages: [
              {
                title: "Read the EULA",
                url: LIBUM_POWERON_PRO_EULA_URL,
                icon: ScrollText,
              },
              {
                title: "Install in Visual Studio Code",
                url: LIBUM_POWERON_PRO_INSTALL_URL,
                icon: MonitorDown,
              },
              {
                title: "Visit Developer Docs",
                url: LIBUM_DOCS_POWERON_PRO_URL,
                icon: CodeXml,
              },
            ],
          },
        ],
      },
    ],
    navResources: [
      {
        title: "Libum Community",
        url: LIBUM_DISCORD_URL,
        icon: Discord,
      },
      {
        title: "Feedback",
        url: LIBUM_FEEDBACK_URL,
        icon: MessageSquareMore,
      },
    ],
    navSecondary: [
      {
        title: "Upgrade to Plus",
        url: "/settings/subscriptions",
        icon: SparkleIcon,
        className: "upgrade-nav",
      },
      {
        title: "Settings",
        url: "/settings",
        icon: Settings,
        className: "settings-nav",
        groups: [
          {
            title: "Personal",
            pages: [
              {
                title: "Your Profile",
                url: "/settings/profile",
                icon: User,
              },
              {
                title: "Organizations",
                url: "/settings/organizations",
                icon: Building2,
              },
            ],
          },
          {
            title: "Organization",
            pages: [
              {
                title: "General",
                url: "/settings/general",
                icon: Building,
              },
              {
                title: "API Keys",
                url: "/settings/api-keys",
                icon: Key,
              },
              {
                title: "Sales Tax",
                url: "/settings/sales-tax",
                icon: Coins,
              },
              {
                title: "Members",
                url: "/settings/members",
                icon: Users,
              },
              {
                title: "Subscriptions",
                url: "/settings/subscriptions",
                icon: CreditCard,
              },
              {
                title: "Danger Zone",
                url: "/settings/danger-zone",
                icon: TriangleAlert,
              },
            ],
          },
          {
            title: "Resources",
            className: "mt-auto",
            pages: [
              {
                title: "Join the Libum Community",
                url: LIBUM_DISCORD_URL,
                icon: Discord,
              },
              {
                title: "Send us a Message",
                url: LIBUM_DEV_MAIL_URL,
                icon: Mail,
              },
            ],
          },
        ],
      },
    ],
  };

  return (
    <Sidebar
      collapsible="icon"
      variant="inset"
      className="overflow-hidden [&>[data-sidebar=sidebar]]:flex-row"
      {...props}
    >
      {/* Primary sidebar */}
      <Sidebar
        collapsible="none"
        className={cn(
          "max-w-12 border-r md:!w-[calc(var(--sidebar-width-icon)_+_1px)]",
        )}
      >
        <SidebarHeader>
          <NavOrganization />
        </SidebarHeader>
        <SidebarContent className="pt-2">
          <NavPrimary items={data.navMain} />
          <Separator className="mx-auto w-8" />
          <NavPrimary items={data.navResources} />
          <NavPrimary
            items={data.navSecondary.filter((i) => {
              if (i.title === "Upgrade to Plus") {
                return !subscriptions.find(
                  (s) =>
                    s.productId ===
                      process.env.NEXT_PUBLIC_STRIPE_POWERON_PRODUCT_ID &&
                    s.status === "active",
                );
              }
              return true;
            })}
            className="mt-auto"
            showUpdateDialog={true}
          />
        </SidebarContent>
        <SidebarFooter>
          <NavUser />
        </SidebarFooter>
      </Sidebar>
      {/* Secondary sidebar - used as drawer on mobile */}
      <Sidebar
        collapsible="none"
        className={cn("flex-1", isMobile && "!w-[calc(100%-4.5rem)]")}
      >
        <SidebarHeader className="gap-3.5 border-b p-4">
          <div className="flex w-full items-center justify-between">
            <div className="font-display text-base font-bold text-foreground">
              {findMainTitleByBaseUrl(data, pathname)}
            </div>
          </div>
        </SidebarHeader>
        <SidebarContent className="lb-scrollbar">
          <NavSecondary items={findGroupsByBaseUrl(data, pathname)} />
        </SidebarContent>
      </Sidebar>
    </Sidebar>
  );
}
